import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { SignaturePad } from '@/components/SignaturePad';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Select } from '@/components/ui/Select';
import { useTechAuth } from '@/contexts/TechAuthContext';
import { useStax } from '@/hooks/useStax';
import { calculateDiscount, formatAmount, validateDiscountCode } from '@/lib/stax';
import {
  createCustomer,
  enrollMembership,
  getScheduleBlocks,
  scheduleMaintenance,
  searchCustomer,
  type EnrollmentRequest,
  type ScheduleMaintenanceResponse,
  type SearchCustomerResponse,
} from '@/lib/memberships-api-client';
import { enqueueOfflineEnrollment, isLikelyOfflineError } from '@/lib/offline-enrollment-queue';

interface PublicConfigCompany {
  id: string | null;
  brand_key: string | null;
  domain?: string | null;
  logo_url?: string | null;
  primary_color?: string | null;
  secondary_color?: string | null;
  terms_url?: string | null;
  enrollment_config?: unknown;
}

interface MembershipPlan {
  id: string;
  plan_code: string;
  plan_name: string;
  description?: string | null;
  base_price_monthly_1_pm: number;
  base_price_annual_1_pm: number;
  base_price_monthly_2_pm: number;
  base_price_annual_2_pm: number;
  additional_system_monthly_1_pm: number;
  additional_system_annual_1_pm: number;
  additional_system_monthly_2_pm: number;
  additional_system_annual_2_pm: number;
  features?: string[] | null;
}

interface DiscountCode {
  id: string;
  code: string;
  code_name: string;
  discount_type: 'percentage' | 'fixed_amount';
  discount_value: number;
  plan_codes: string[] | null;
  applies_to_billing: string[] | null;
  valid_from: string | null;
  valid_until: string | null;
  is_active: boolean;
  description: string;
  terms_conditions: string;
}

type CustomerMatch = SearchCustomerResponse['customers'][number];

type Step = 'customer' | 'plan' | 'payment' | 'signature' | 'complete';

type PublicConfigResponse = {
  company: PublicConfigCompany;
  membership_plans: MembershipPlan[];
  discount_codes: DiscountCode[];
};

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function normalizePhone(raw: string): string {
  const digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) return digits.slice(1);
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

function toLocalIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function successwareKeyFromCompany(companyId: string | null | undefined, brandKey: string | null | undefined): string | null {
  const bk = String(brandKey || '').trim().toLowerCase();
  if (bk === 'hales' || bk === 'climate' || bk === 'abraham') return bk;

  const id = String(companyId || '').trim().toLowerCase();
  if (id.includes('hales')) return 'hales';
  if (id.includes('abraham')) return 'abraham';
  if (id.includes('climate') || id.includes('my-ccs') || id.includes('ccs')) return 'climate';

  return null;
}

export default function TechEnrollPage() {
  // technician may be null for public customer self-enrollment (memberships.*.com)
  const { technician, refreshPendingEnrollmentCount } = useTechAuth();

  const [step, setStep] = useState<Step>('customer');
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  // Payment form ref for Stax tokenization
  const paymentFormRef = useRef<HTMLFormElement>(null);
  const { isStaxReady, cardFormStatus, staxError, setBrandKey, tokenizePaymentMethod } = useStax();

  // Public config (company + plans + discount codes)
  const domain = typeof window !== 'undefined' ? window.location.host : '';

  const {
    data: publicConfig,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    queryKey: ['tech-public-config', domain],
    enabled: Boolean(domain),
    queryFn: async () => {
      const resp = await fetch(`/api/memberships/public-config?domain=${encodeURIComponent(domain)}`);
      let payload: unknown = null;
      try {
        payload = await resp.json();
      } catch {
        payload = null;
      }

      if (!resp.ok || !payload) {
        const msg = (payload && typeof payload === 'object' && 'error' in payload)
          ? String((payload as any).error)
          : `Failed to load config (${resp.status})`;
        throw new Error(msg);
      }

      return payload as PublicConfigResponse;
    },
    staleTime: 60_000,
  });

  const companyId = technician?.company_id || publicConfig?.company?.id || null;

  const successwareCompany = useMemo(() => {
    return successwareKeyFromCompany(companyId, publicConfig?.company?.brand_key);
  }, [companyId, publicConfig?.company?.brand_key]);

  const membershipPlans = publicConfig?.membership_plans || [];
  const discountCodes = publicConfig?.discount_codes || [];

  // ---------------------------------------------------------------------------
  // Customer search/create
  // ---------------------------------------------------------------------------

  const [phone, setPhone] = useState('');
  const [matches, setMatches] = useState<CustomerMatch[]>([]);
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(0);
  const [showNewCustomerForm, setShowNewCustomerForm] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('FL');
  const [zip, setZip] = useState('');

  const [successwareCustomerId, setSuccesswareCustomerId] = useState('');
  const [successwareServiceAccountId, setSuccesswareServiceAccountId] = useState('');
  const [successwareLocationId, setSuccesswareLocationId] = useState('');

  function formatCustomerMatchOption(match: CustomerMatch): string {
    const name = [match.firstName, match.lastName].filter(Boolean).join(' ').trim();
    const primary = name || match.email || match.address || (match.customerId ? `Customer ${match.customerId}` : 'Customer');

    const parts = [
      primary,
      match.phone,
      match.address,
      match.locationId ? `Loc ${match.locationId}` : '',
    ]
      .map((p) => String(p || '').trim())
      .filter(Boolean);

    const seen = new Set<string>();
    const unique = parts.filter((p) => {
      if (seen.has(p)) return false;
      seen.add(p);
      return true;
    });

    return unique.join(' • ');
  }

  function pickBestCustomerMatchIndex(customers: CustomerMatch[]): number {
    let bestIdx = 0;
    let bestScore = -1;

    for (let idx = 0; idx < customers.length; idx++) {
      const c = customers[idx];
      let score = 0;
      if (c.firstName || c.lastName) score += 10;
      if (c.email) score += 5;
      if (c.address) score += 3;
      if (c.city) score += 1;
      if (c.state) score += 1;
      if (c.zip) score += 1;

      if (score > bestScore) {
        bestScore = score;
        bestIdx = idx;
      }
    }

    return bestIdx;
  }

  function applyCustomerMatch(match: CustomerMatch) {
    setSuccesswareCustomerId(match.customerId);
    setSuccesswareServiceAccountId(match.serviceAccountId);
    setSuccesswareLocationId(match.locationId);

    setPhone(match.phone || phone);
    if (match.firstName) setFirstName(match.firstName);
    if (match.lastName) setLastName(match.lastName);
    if (match.email) setEmail(match.email);
    if (match.address) setAddress(match.address);
    if (match.city) setCity(match.city);
    if (match.state) setState(match.state);
    if (match.zip) setZip(match.zip);
  }

  async function handleSearchCustomer() {
    setError(null);

    if (!successwareCompany) {
      setError('Company is not fully configured for SuccessWare.');
      return;
    }

    const cleanedPhone = normalizePhone(phone);
    if (cleanedPhone.length < 10) {
      setError('Enter a full 10-digit phone number for customer lookup.');
      return;
    }

    setIsBusy(true);
    try {
      const res = await searchCustomer({
        company: successwareCompany,
        phone: cleanedPhone,
      });

      const customers = res.customers || [];
      setMatches(customers);

      if (customers.length) {
        const bestIdx = pickBestCustomerMatchIndex(customers);
        setSelectedMatchIndex(bestIdx);
        applyCustomerMatch(customers[bestIdx] || customers[0]);
        setShowNewCustomerForm(false);
      } else {
        setSelectedMatchIndex(0);
        setShowNewCustomerForm(true);
      }
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleCreateCustomer() {
    setError(null);

    if (!successwareCompany) {
      setError('Company is not fully configured for SuccessWare.');
      return;
    }

    const cleanedPhone = normalizePhone(phone);

    if (!firstName || !lastName || !email || cleanedPhone.length < 10 || !address || !city || !state || !zip) {
      setError('Fill in first/last name, email, phone, and full address.');
      return;
    }

    setIsBusy(true);
    try {
      const res = await createCustomer({
        company: successwareCompany,
        customer: {
          firstName,
          lastName,
          email,
          phone: cleanedPhone,
        },
        location: {
          address1: address,
          city,
          state,
          zipCode: zip,
        },
      });

      setSuccesswareCustomerId(res.customerId);
      setSuccesswareServiceAccountId(res.serviceAccountId);
      setSuccesswareLocationId(res.locationId);

      setStep('plan');
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Plan selection
  // ---------------------------------------------------------------------------

  const prefill = useMemo(() => {
    if (typeof window === 'undefined') {
      return {
        planId: '',
        billingFrequency: 'monthly' as const,
        pmFrequency: 2 as const,
        systemsCount: 1,
        discountCode: '',
        hasDiscountPrefill: false,
      };
    }

    const params = new URLSearchParams(window.location.search);

    const planId = String(params.get('planId') || '').trim();
    const billingFrequency = params.get('billingFrequency') === 'annual' ? 'annual' : 'monthly';

    const pmRaw = String(params.get('pmFrequency') || '').trim();
    const pmFrequency = pmRaw === '1' ? 1 : 2;

    const systemsRaw = Number(params.get('systemsCount') || 1);
    const systemsCount = Number.isFinite(systemsRaw) ? Math.min(5, Math.max(1, Math.floor(systemsRaw))) : 1;

    const discountRaw = String(params.get('discount') || '').trim();
    const discountCode = discountRaw ? discountRaw.toUpperCase() : '';

    return {
      planId,
      billingFrequency: billingFrequency as 'monthly' | 'annual',
      pmFrequency: pmFrequency as 1 | 2,
      systemsCount,
      discountCode,
      hasDiscountPrefill: Boolean(discountRaw),
    };
  }, []);

  const [planId, setPlanId] = useState(prefill.planId);
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'annual'>(prefill.billingFrequency);
  const [pmFrequency, setPmFrequency] = useState<1 | 2>(prefill.pmFrequency);
  const [systemsCount, setSystemsCount] = useState(prefill.systemsCount);

  const [discountCode, setDiscountCode] = useState(prefill.discountCode);
  const [appliedDiscount, setAppliedDiscount] = useState<DiscountCode | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);

  const didApplyPrefillDiscount = useRef(false);

  const selectedPlan = useMemo(() => {
    return membershipPlans.find((p) => p.id === planId) || null;
  }, [membershipPlans, planId]);

  function calculateBasePrice(plan: MembershipPlan): number {
    let basePrice: number;

    if (pmFrequency === 1) {
      basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_1_pm : plan.base_price_annual_1_pm;
    } else {
      basePrice = billingFrequency === 'monthly' ? plan.base_price_monthly_2_pm : plan.base_price_annual_2_pm;
    }

    if (systemsCount > 1) {
      let additionalPrice: number;

      if (pmFrequency === 1) {
        additionalPrice =
          billingFrequency === 'monthly' ? plan.additional_system_monthly_1_pm : plan.additional_system_annual_1_pm;
      } else {
        additionalPrice =
          billingFrequency === 'monthly' ? plan.additional_system_monthly_2_pm : plan.additional_system_annual_2_pm;
      }

      basePrice += additionalPrice * (systemsCount - 1);
    }

    return basePrice;
  }

  const pricing = useMemo(() => {
    if (!selectedPlan) {
      return { basePrice: 0, discountAmount: 0, finalPrice: 0 };
    }

    const basePrice = calculateBasePrice(selectedPlan);
    if (!appliedDiscount) {
      return { basePrice, discountAmount: 0, finalPrice: basePrice };
    }

    const discountAmount = calculateDiscount(basePrice, appliedDiscount.discount_type, appliedDiscount.discount_value);
    return {
      basePrice,
      discountAmount,
      finalPrice: Math.max(0, basePrice - discountAmount),
    };
  }, [selectedPlan, billingFrequency, pmFrequency, systemsCount, appliedDiscount]);

  async function handleApplyDiscount() {
    setDiscountError(null);

    const raw = discountCode.trim();
    if (!raw) return;

    if (!selectedPlan) {
      setDiscountError('Select a plan first.');
      return;
    }

    const found = discountCodes.find((c) => String(c.code || '').trim().toUpperCase() === raw.toUpperCase()) || null;
    if (!found) {
      setDiscountError('Invalid discount code');
      return;
    }

    const validation = validateDiscountCode(found, selectedPlan.plan_code, billingFrequency);
    if (!validation.valid) {
      setDiscountError(validation.reason || 'Invalid discount code');
      return;
    }

    setAppliedDiscount(found);
    setDiscountError(null);
  }

  // ---------------------------------------------------------------------------
  // Payment + enrollment
  // ---------------------------------------------------------------------------

  const [cardMonth, setCardMonth] = useState('');
  const [cardYear, setCardYear] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);

  const [paymentToken, setPaymentToken] = useState<string | null>(null);
  const [signatureDataUrl, setSignatureDataUrl] = useState('');
  const [signatureIsEmpty, setSignatureIsEmpty] = useState(true);

  const [enrollResult, setEnrollResult] = useState<{ membership_id: string } | null>(null);
  const [queuedEnrollmentId, setQueuedEnrollmentId] = useState<string | null>(null);

  // Post-enrollment scheduling
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<{ date: string; window: string } | null>(null);
  const [equipmentAge, setEquipmentAge] = useState('');
  const [scheduleResult, setScheduleResult] = useState<ScheduleMaintenanceResponse | null>(null);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleBusy, setScheduleBusy] = useState(false);

  const scheduleBlocksQuery = useQuery({
    queryKey: ['tech-enroll-schedule-blocks', successwareCompany],
    enabled: step === 'complete' && scheduleOpen && Boolean(successwareCompany),
    queryFn: async () => {
      if (!successwareCompany) throw new Error('Company is not configured for scheduling.');

      const start = new Date();
      const end = addDays(start, 14);

      return getScheduleBlocks({
        company: successwareCompany,
        jobClass: 'HVACMaintenance',
        jobType: 'PTU',
        startDate: toLocalIsoDate(start),
        endDate: toLocalIsoDate(end),
      });
    },
    staleTime: 60_000,
  });

  const scheduleDates = useMemo(() => {
    const slots = scheduleBlocksQuery.data?.availableSlots || [];
    const byDate: Record<string, { AM: boolean; PM: boolean }> = {};

    for (const slot of slots) {
      const date = String(slot.date || '').trim();
      const window = String(slot.window || '').trim().toUpperCase();
      if (!date) continue;
      if (window !== 'AM' && window !== 'PM') continue;

      if (!byDate[date]) {
        byDate[date] = { AM: false, PM: false };
      }
      byDate[date][window as 'AM' | 'PM'] = Boolean(slot.available);
    }

    return Object.entries(byDate)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, windows]) => ({ date, windows }));
  }, [scheduleBlocksQuery.data]);

  async function handleScheduleMaintenance() {
    setScheduleError(null);

    const membershipId = enrollResult?.membership_id;
    if (!membershipId) {
      setScheduleError('Missing membership id.');
      return;
    }

    if (!selectedSlot) {
      setScheduleError('Select a date/time window first.');
      return;
    }

    setScheduleBusy(true);
    try {
      const result = await scheduleMaintenance({
        membership_id: membershipId,
        visit_date: selectedSlot.date,
        visit_window: selectedSlot.window,
        visit_type: 'pm',
        equipment_age: equipmentAge.trim() ? equipmentAge.trim() : undefined,
        priority_level: 1,
      });

      setScheduleResult(result);
    } catch (e) {
      setScheduleError(getErrorMessage(e));
    } finally {
      setScheduleBusy(false);
    }
  }

  async function handleContinueToSignature() {
    setError(null);

    if (!companyId) {
      setError('Company could not be resolved.');
      return;
    }

    if (!selectedPlan) {
      setError('Select a plan.');
      return;
    }

    if (!successwareCustomerId || !successwareServiceAccountId || !successwareLocationId) {
      setError('Search/select (or create) the customer first.');
      return;
    }

    if (!email || !firstName || !lastName || !phone) {
      setError('Customer email, first name, last name, and phone are required.');
      return;
    }

    if (!address || !city || !state || !zip) {
      setError('Customer address is required.');
      return;
    }

    if (!termsAccepted) {
      setError('Customer must accept the terms and conditions.');
      return;
    }

    if (!isStaxReady || cardFormStatus !== 'mounted') {
      setError('Payment system is still loading. Please wait a moment and try again.');
      return;
    }

    if (!cardMonth || !cardYear) {
      setError('Enter card expiration month and year.');
      return;
    }

    if (!paymentFormRef.current) {
      setError('Payment form is not ready. Please refresh and try again.');
      return;
    }

    if (!successwareCompany) {
      setError('Company is not fully configured for payment tokenization.');
      return;
    }

    setIsBusy(true);
    try {
      setBrandKey(successwareCompany);

      const token = await tokenizePaymentMethod({
        form: paymentFormRef.current,
        extraDetails: {
          firstname: firstName,
          lastname: lastName,
          email,
          phone,
          address_1: address,
          address_city: city,
          address_state: state,
          address_zip: zip,
        },
        brandKey: successwareCompany,
      });

      setPaymentToken(token);
      setSignatureDataUrl('');
      setSignatureIsEmpty(true);
      setStep('signature');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  }

  async function handleCompleteEnrollment() {
    setError(null);

    if (!companyId) {
      setError('Company could not be resolved.');
      return;
    }

    if (!selectedPlan) {
      setError('Select a plan.');
      return;
    }

    if (!successwareCustomerId || !successwareServiceAccountId || !successwareLocationId) {
      setError('Search/select (or create) the customer first.');
      return;
    }

    if (!email || !firstName || !lastName || !phone) {
      setError('Customer email, first name, last name, and phone are required.');
      return;
    }

    if (!address || !city || !state || !zip) {
      setError('Customer address is required.');
      return;
    }

    if (!termsAccepted) {
      setError('Customer must accept the terms and conditions.');
      return;
    }

    if (!paymentToken) {
      setError('Payment info is missing. Please go back and re-enter payment details.');
      return;
    }

    if (signatureIsEmpty || !signatureDataUrl) {
      setError('Customer signature is required.');
      return;
    }

    setIsBusy(true);
    let payload: EnrollmentRequest | null = null;

    try {
      const signedAt = new Date().toISOString();
      const signedName = [firstName, lastName].filter(Boolean).join(' ').trim() || undefined;

      // Use 'public_signup' when customer is self-enrolling (no technician logged in)
      const enrollmentSource = technician ? 'tech_portal' : 'public_signup';

      payload = {
        company_id: companyId,
        enrollment_source: enrollmentSource,
        plan_id: selectedPlan.id,
        plan_code: selectedPlan.plan_code,
        plan_name: selectedPlan.plan_name,
        billing_frequency: billingFrequency,
        tune_ups_per_year: pmFrequency,
        systems_count: systemsCount,
        base_price_cents: Math.round(pricing.basePrice),
        discount_code_id: appliedDiscount?.id,
        discount_amount_cents: appliedDiscount ? Math.round(pricing.discountAmount) : undefined,
        final_price_cents: Math.round(pricing.finalPrice),
        customer: {
          email,
          first_name: firstName,
          last_name: lastName,
          phone: normalizePhone(phone),
          address,
          city,
          state,
          zip,
        },
        successware: {
          customer_id: successwareCustomerId,
          service_account_id: successwareServiceAccountId,
          location_id: successwareLocationId,
        },
        payment_token: paymentToken,
        terms_accepted: {
          timestamp: signedAt,
          terms_version: 'tech_portal',
          signature: {
            data_url: signatureDataUrl,
            signed_name: signedName,
            signed_at: signedAt,
          },
        },
      };

      const result = await enrollMembership(payload);
      setQueuedEnrollmentId(null);
      setEnrollResult({ membership_id: result.membership_id });
      setStep('complete');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      if (payload && isLikelyOfflineError(e)) {
        try {
          const queued = await enqueueOfflineEnrollment(payload);
          setQueuedEnrollmentId(queued.id);
          setEnrollResult(null);

          // Scheduling requires a real membership id, so keep this closed until sync completes.
          setScheduleOpen(false);
          setSelectedSlot(null);
          setScheduleResult(null);
          setScheduleError(null);
          setScheduleBusy(false);

          await refreshPendingEnrollmentCount();
          setStep('complete');
          window.scrollTo({ top: 0, behavior: 'smooth' });
          return;
        } catch (queueErr) {
          setError(getErrorMessage(queueErr));
          return;
        }
      }

      setError(getErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  }

  const canContinueFromCustomer = Boolean(successwareCustomerId && successwareServiceAccountId && successwareLocationId);

  useEffect(() => {
    // If the user changes plans/billing, clear discount (because applies_to_billing/plan_codes may change)
    setAppliedDiscount(null);
    setDiscountError(null);
  }, [planId, billingFrequency]);

  useEffect(() => {
    if (!prefill.hasDiscountPrefill) return;
    if (didApplyPrefillDiscount.current) return;
    if (!selectedPlan) return;

    didApplyPrefillDiscount.current = true;

    const raw = prefill.discountCode.trim();
    if (!raw) return;

    const found =
      discountCodes.find((c) => String(c.code || '').trim().toUpperCase() === raw.toUpperCase()) || null;
    if (!found) {
      setDiscountError('Invalid discount code');
      return;
    }

    const validation = validateDiscountCode(found, selectedPlan.plan_code, billingFrequency);
    if (!validation.valid) {
      setDiscountError(validation.reason || 'Invalid discount code');
      return;
    }

    setAppliedDiscount(found);
    setDiscountError(null);
  }, [billingFrequency, discountCodes, prefill.discountCode, prefill.hasDiscountPrefill, selectedPlan]);

  if (configLoading) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="text-gray-600">Loading enrollment config…</div>
      </div>
    );
  }

  if (configError || !publicConfig) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="text-red-600">{getErrorMessage(configError) || 'Failed to load enrollment config.'}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="bg-white border border-gray-200 rounded-2xl p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enroll Customer</h1>
            <div className="text-sm text-gray-600">
              Company: <span className="font-mono">{companyId || '—'}</span>
            </div>
          </div>

          <div className="text-xs text-gray-500">Step: {step}</div>
        </div>
      </div>

      {step === 'customer' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">1) Customer</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label htmlFor="phone">Customer phone (search)</Label>
              <Input
                id="phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="(555) 555-5555"
              />
            </div>
            <Button variant="secondary" disabled={isBusy} onClick={handleSearchCustomer}>
              {isBusy ? 'Searching…' : 'Search'}
            </Button>
          </div>

          {matches.length > 0 && !showNewCustomerForm && (
            <div className="space-y-2">
              <div className="text-sm font-semibold">Matches</div>
              {matches.length > 1 && (
                <div className="text-xs text-gray-500">
                  Multiple accounts found for this phone — select the correct customer/location.
                </div>
              )}
              <Select
                value={String(selectedMatchIndex)}
                onChange={(e) => {
                  const idx = Number(e.target.value);
                  setSelectedMatchIndex(idx);
                  const match = matches[idx];
                  if (match) applyCustomerMatch(match);
                }}
              >
                {matches.map((m, idx) => (
                  <option key={`${m.customerId}-${m.locationId}`} value={String(idx)}>
                    {formatCustomerMatchOption(m)}
                  </option>
                ))}
              </Select>

              <div className="flex justify-end">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setShowNewCustomerForm(true);
                    setMatches([]);
                    setSelectedMatchIndex(0);
                    setSuccesswareCustomerId('');
                    setSuccesswareServiceAccountId('');
                    setSuccesswareLocationId('');
                  }}
                >
                  Create new customer instead
                </Button>
              </div>
            </div>
          )}

          {showNewCustomerForm && (
            <div className="space-y-3">
              <div className="text-sm font-semibold">New customer details</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="firstName">First name</Label>
                  <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="lastName">Last name</Label>
                  <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="phone2">Phone</Label>
                  <Input id="phone2" value={phone} onChange={(e) => setPhone(e.target.value)} />
                </div>
              </div>
              <div>
                <Label htmlFor="address">Address</Label>
                <Input id="address" value={address} onChange={(e) => setAddress(e.target.value)} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input id="city" value={city} onChange={(e) => setCity(e.target.value)} />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input id="state" value={state} onChange={(e) => setState(e.target.value)} maxLength={2} />
                </div>
                <div>
                  <Label htmlFor="zip">ZIP</Label>
                  <Input id="zip" value={zip} onChange={(e) => setZip(e.target.value)} />
                </div>
              </div>

              <div className="flex justify-end">
                <Button variant="primary" disabled={isBusy} onClick={handleCreateCustomer}>
                  {isBusy ? 'Creating…' : 'Create customer'}
                </Button>
              </div>
            </div>
          )}

          {!showNewCustomerForm && canContinueFromCustomer && (
            <div className="pt-4 border-t flex justify-end">
              <Button variant="primary" onClick={() => setStep('plan')}>
                Next: Plan
              </Button>
            </div>
          )}
        </div>
      )}

      {step === 'plan' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">2) Plan</h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="plan">Plan</Label>
              <Select
                id="plan"
                value={planId}
                onChange={(e) => {
                  setPlanId(e.target.value);
                  setAppliedDiscount(null);
                  setDiscountError(null);
                }}
              >
                <option value="">Select a plan</option>
                {membershipPlans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plan_name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="billing">Billing</Label>
              <Select
                id="billing"
                value={billingFrequency}
                onChange={(e) => setBillingFrequency(e.target.value as 'monthly' | 'annual')}
              >
                <option value="monthly">Monthly</option>
                <option value="annual">Annual</option>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="pm">PM visits / year</Label>
              <Select id="pm" value={String(pmFrequency)} onChange={(e) => setPmFrequency(Number(e.target.value) as 1 | 2)}>
                <option value="1">1</option>
                <option value="2">2</option>
              </Select>
            </div>
            <div>
              <Label htmlFor="systems">Systems</Label>
              <Input
                id="systems"
                inputMode="numeric"
                value={String(systemsCount)}
                onChange={(e) => {
                  const raw = e.target.value.replace(/[^0-9]/g, '');
                  const n = raw ? Number(raw) : 1;
                  setSystemsCount(Number.isFinite(n) && n > 0 ? n : 1);
                }}
              />
            </div>
          </div>

          <div className="border rounded-lg p-3 bg-gray-50">
            <div className="flex justify-between text-sm">
              <span>Base price</span>
              <span>{formatAmount(pricing.basePrice)}</span>
            </div>
            {appliedDiscount && (
              <div className="flex justify-between text-sm text-green-700">
                <span>Discount ({appliedDiscount.code})</span>
                <span>-{formatAmount(pricing.discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between text-sm font-semibold">
              <span>Final price</span>
              <span>{formatAmount(pricing.finalPrice)}</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="md:col-span-2">
              <Label htmlFor="discount">Discount code (optional)</Label>
              <Input
                id="discount"
                value={discountCode}
                onChange={(e) => {
                  setDiscountCode(e.target.value.toUpperCase());
                  setDiscountError(null);
                }}
              />
              {discountError && <div className="text-xs text-red-600 mt-1">{discountError}</div>}
            </div>
            <div className="flex gap-2">
              <Button variant="secondary" disabled={!discountCode || !selectedPlan} onClick={handleApplyDiscount}>
                Apply
              </Button>
              {appliedDiscount && (
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAppliedDiscount(null);
                    setDiscountError(null);
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>

          <div className="pt-4 border-t flex justify-between">
            <Button variant="secondary" onClick={() => setStep('customer')}>
              Back
            </Button>
            <Button
              variant="primary"
              disabled={!selectedPlan}
              onClick={() => {
                setError(null);
                if (!selectedPlan) {
                  setError('Select a plan.');
                  return;
                }
                setStep('payment');
              }}
            >
              Next: Payment
            </Button>
          </div>
        </div>
      )}

      {step === 'payment' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">3) Payment</h2>

          <form ref={paymentFormRef} className="space-y-3" onSubmit={(e) => e.preventDefault()}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <Label>Card number</Label>
                <div id="card-number" className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center" />
              </div>
              <div>
                <Label>CVV</Label>
                <div id="card-cvv" className="h-10 px-3 border border-gray-300 rounded-md bg-white flex items-center" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="month">Exp Month</Label>
                <Input
                  id="month"
                  name="month"
                  placeholder="MM"
                  value={cardMonth}
                  onChange={(e) => setCardMonth(e.target.value.slice(0, 2))}
                  inputMode="numeric"
                  maxLength={2}
                />
              </div>
              <div>
                <Label htmlFor="year">Exp Year</Label>
                <Input
                  id="year"
                  name="year"
                  placeholder="YYYY"
                  value={cardYear}
                  onChange={(e) => setCardYear(e.target.value.slice(0, 4))}
                  inputMode="numeric"
                  maxLength={4}
                />
              </div>
              <div className="text-xs text-gray-500 flex items-center">
                {cardFormStatus === 'loading' && 'Loading secure fields…'}
                {cardFormStatus === 'error' && staxError && <span className="text-red-500">{staxError}</span>}
                {cardFormStatus === 'mounted' && <span className="text-green-600">Payment form ready.</span>}
                {!isStaxReady && 'Loading payment system…'}
              </div>
            </div>

            <div className="pt-2 border-t space-y-2">
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className="mt-1"
                />
                <span>
                  Customer agrees to the terms and conditions.
                  {publicConfig.company?.terms_url && (
                    <>
                      {' '}
                      (<a className="text-blue-600 underline" href={publicConfig.company.terms_url} target="_blank" rel="noreferrer">view</a>)
                    </>
                  )}
                </span>
              </label>
            </div>
          </form>

          <div className="pt-4 border-t flex justify-between">
            <Button variant="secondary" onClick={() => setStep('plan')}>
              Back
            </Button>
            <Button variant="primary" disabled={isBusy} onClick={handleContinueToSignature}>
              {isBusy ? 'Securing payment…' : `Next: Signature (${formatAmount(pricing.finalPrice)})`}
            </Button>
          </div>
        </div>
      )}

      {step === 'signature' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-4">
          <h2 className="text-lg font-semibold text-gray-900">4) Signature</h2>

          <div className="text-sm text-gray-700">
            Have the customer sign to authorize enrollment and payment of{' '}
            <span className="font-semibold">{formatAmount(pricing.finalPrice)}</span>.
          </div>

          <SignaturePad
            onChange={(value) => {
              setSignatureDataUrl(value.dataUrl);
              setSignatureIsEmpty(value.isEmpty);
            }}
          />

          <div className="pt-4 border-t flex justify-between">
            <Button
              variant="secondary"
              disabled={isBusy}
              onClick={() => {
                setPaymentToken(null);
                setSignatureDataUrl('');
                setSignatureIsEmpty(true);
                setStep('payment');
              }}
            >
              Back
            </Button>
            <Button
              variant="primary"
              disabled={isBusy || !paymentToken || signatureIsEmpty}
              onClick={handleCompleteEnrollment}
            >
              {isBusy ? 'Enrolling…' : 'Complete Enrollment →'}
            </Button>
          </div>
        </div>
      )}

      {step === 'complete' && (
        <div className="bg-white border border-gray-200 rounded-2xl p-6 space-y-3">
          <h2 className="text-xl font-extrabold text-gray-900">
            {queuedEnrollmentId ? 'Enrollment saved (pending sync)' : 'Enrollment complete'}
          </h2>

          {queuedEnrollmentId ? (
            <div className="text-sm text-gray-700">
              This enrollment has been saved on this device and will sync automatically when you're back online.
            </div>
          ) : (
            <div className="text-sm text-gray-700">
              Membership created: <span className="font-mono">{enrollResult?.membership_id || '—'}</span>
            </div>
          )}

          <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
            <div className="text-sm font-semibold text-gray-900">What happens next</div>
            <ul className="mt-2 list-disc pl-5 text-sm text-gray-700 space-y-1">
              {queuedEnrollmentId ? (
                <>
                  <li>We'll sync the enrollment automatically once you're online again.</li>
                  <li>Welcome email + terms PDF will send after the sync completes.</li>
                  <li>If payment fails during sync, you’ll see an alert and can retry with a different card.</li>
                </>
              ) : (
                <>
                  <li>Welcome email + terms PDF are sent automatically.</li>
                  <li>Customer can access the member portal from the welcome email.</li>
                  <li>Schedule the first tune-up before you leave (recommended).</li>
                </>
              )}
            </ul>
          </div>

          {!queuedEnrollmentId && (
            <div className="flex flex-col sm:flex-row gap-2">
              <Button
                variant="primary"
                onClick={() => {
                  setScheduleOpen(true);
                  setSelectedSlot(null);
                  setScheduleResult(null);
                  setScheduleError(null);
                }}
              >
                Schedule first tune-up
              </Button>
              <Button
                variant="secondary"
                onClick={() => {
                  setScheduleOpen(false);
                  setSelectedSlot(null);
                  setScheduleResult(null);
                  setScheduleError(null);
                  setEquipmentAge('');
                }}
              >
                Skip for now
              </Button>
            </div>
          )}

          {!queuedEnrollmentId && scheduleOpen && (
            <div className="rounded-2xl border border-gray-200 p-4 space-y-3">
              <div className="text-sm font-semibold text-gray-900">Pick a time window</div>

              {scheduleBlocksQuery.isLoading ? (
                <div className="text-sm text-gray-600">Loading available slots…</div>
              ) : scheduleBlocksQuery.error ? (
                <div className="text-sm text-red-600">{getErrorMessage(scheduleBlocksQuery.error)}</div>
              ) : scheduleDates.length ? (
                <div className="space-y-2">
                  {scheduleDates.slice(0, 10).map(({ date, windows }) => {
                    const selectedAm = selectedSlot?.date === date && selectedSlot?.window === 'AM';
                    const selectedPm = selectedSlot?.date === date && selectedSlot?.window === 'PM';
                    return (
                      <div key={date} className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold text-gray-800">{date}</div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            disabled={!windows.AM}
                            onClick={() => setSelectedSlot({ date, window: 'AM' })}
                            className={[
                              'px-3 py-2 rounded-lg border text-sm font-semibold',
                              windows.AM ? 'bg-white hover:bg-gray-50 border-gray-300 text-gray-900' : 'bg-gray-100 border-gray-200 text-gray-400',
                              selectedAm ? 'ring-2 ring-blue-500' : '',
                            ].join(' ')}
                          >
                            AM
                          </button>
                          <button
                            type="button"
                            disabled={!windows.PM}
                            onClick={() => setSelectedSlot({ date, window: 'PM' })}
                            className={[
                              'px-3 py-2 rounded-lg border text-sm font-semibold',
                              windows.PM ? 'bg-white hover:bg-gray-50 border-gray-300 text-gray-900' : 'bg-gray-100 border-gray-200 text-gray-400',
                              selectedPm ? 'ring-2 ring-blue-500' : '',
                            ].join(' ')}
                          >
                            PM
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  <div className="text-xs text-gray-500">Showing the next 10 days with available windows.</div>
                </div>
              ) : (
                <div className="text-sm text-gray-600">No available slots returned.</div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="equipmentAge">Equipment age (optional)</Label>
                  <Input
                    id="equipmentAge"
                    value={equipmentAge}
                    onChange={(e) => setEquipmentAge(e.target.value)}
                    placeholder="e.g. 8 years"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    disabled={scheduleBusy || !selectedSlot}
                    onClick={handleScheduleMaintenance}
                  >
                    {scheduleBusy ? 'Booking…' : 'Book tune-up'}
                  </Button>
                </div>
              </div>

              {scheduleError ? <div className="text-sm text-red-600">{scheduleError}</div> : null}

              {scheduleResult?.success ? (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-900">
                  <div className="font-semibold">Scheduled!</div>
                  <div className="mt-1 text-sm">
                    Job #: <span className="font-mono">{scheduleResult.job_number || '—'}</span>
                  </div>
                  <div className="mt-1 text-sm">
                    Remaining PM visits: <span className="font-semibold">{scheduleResult.visits_remaining}</span>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <div className="pt-4 border-t flex gap-2">
            <Button
              variant="primary"
              onClick={() => {
                // reset for next enrollment
                setStep('customer');
                setError(null);
                setIsBusy(false);
                setMatches([]);
                setSelectedMatchIndex(0);
                setShowNewCustomerForm(false);
                setPhone('');
                setFirstName('');
                setLastName('');
                setEmail('');
                setAddress('');
                setCity('');
                setState('FL');
                setZip('');
                setSuccesswareCustomerId('');
                setSuccesswareServiceAccountId('');
                setSuccesswareLocationId('');
                setPlanId('');
                setBillingFrequency('monthly');
                setPmFrequency(2);
                setSystemsCount(1);
                setDiscountCode('');
                setAppliedDiscount(null);
                setDiscountError(null);
                setCardMonth('');
                setCardYear('');
                setTermsAccepted(false);
                setPaymentToken(null);
                setSignatureDataUrl('');
                setSignatureIsEmpty(true);
                setScheduleOpen(false);
                setSelectedSlot(null);
                setEquipmentAge('');
                setScheduleResult(null);
                setScheduleError(null);
                setScheduleBusy(false);
                setEnrollResult(null);
                setQueuedEnrollmentId(null);
              }}
            >
              Start another enrollment
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
